import { useState, type ComponentProps } from 'react'
import { FieldInfo, hasFieldError, useFieldContext, useFormReadOnly, useIsFieldOptional } from './shared'
import { Input } from '../../ui/input'
import { FieldLabelRow } from './field-label-row'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

export default function PasswordField({
  label,
  className,
  optional,
  ...props
}: Omit<ComponentProps<'input'>, 'type'> & { label: string; optional?: boolean }) {
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isReadOnly = props.readOnly ?? formReadOnly
  const isOptional = useIsFieldOptional(field.name, optional)
  const [showPassword, setShowPassword] = useState(false)
  const value = field.state.value
  const displayValue = isReadOnly && !value ? '-' : value

  return (
    <div className={className}>
      <div className="grid gap-2">
        {label && (
          <FieldLabelRow
            label={label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
          />
        )}
        <div className="relative">
          <Input
            id={field.name}
            name={field.name}
            type={showPassword || isReadOnly ? 'text' : 'password'}
            aria-invalid={hasFieldError(field.state.meta)}
            value={displayValue}
            onBlur={field.handleBlur}
            onChange={(e) => field.handleChange(e.target.value)}
            {...props}
            readOnly={isReadOnly}
            tabIndex={isReadOnly ? -1 : props.tabIndex}
            className={cn(
              !isReadOnly && 'pr-10',
              isReadOnly &&
                'border-transparent bg-transparent px-0 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent',
            )}
          />
          {!isReadOnly && (
            <ButtonPrimitive
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon className="size-5" /> : <EyeIcon className="size-5" />}
            </ButtonPrimitive>
          )}
        </div>
      </div>
      <FieldInfo fieldState={field.state} />
    </div>
  )
}
